import { useNavigate, useParams } from "react-router-dom";

import { useUserData } from "store/slices/authSlice";
import { NotificationRaw } from "types/notification";
import { encode, formatCurrency } from "utils/HelperFunctions";

/**
 * Which preview a notification opens when its highlighted words are clicked.
 * The caller decides *how* to open it: the header panel hands this to the
 * app-wide drawers, the notifications page drives its own local drawers.
 */
export type NotificationPreviewType =
  | "agreement"
  | "payment_link"
  | "payment"
  | "invoice"
  | "quotes"
  | "subscription";

interface NotificationMessageProps {
  data: NotificationRaw;
  onOpenPreview: (type: NotificationPreviewType, id: string | number) => void;
}

/**
 * The wording of a single notification.
 *
 * This used to exist twice — once in the header panel and once on the
 * notifications page — roughly 1,600 near-identical lines each. The two
 * drifted apart, so the same sentence could read differently depending on
 * where you saw it. One copy now serves both.
 */
const NotificationMessage = ({
  data,
  onOpenPreview,
}: NotificationMessageProps) => {
  const userData = useUserData();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const accountData = userData?.data?.accountData;

  // Kept on one line: the browser may otherwise break "(2026-43128)" at the
  // hyphen, leaving "(2026-" at the end of one line and "43128)" on the next.
  const accountIdLabel = (
    <span className="whitespace-nowrap">({accountData?.accountid})</span>
  );

  // Some events arrive with no order id and no amount — a third of failed
  // retries in the sample data look like this. Printing "payment #0 for $0.00"
  // tells the customer nothing, and the reference would link to a preview of a
  // payment that does not exist, so both are simply left out.
  const hasOrderId = Boolean(data?.order_id);
  const hasAmount =
    data?.payment_amount !== null &&
    data?.payment_amount !== undefined &&
    Number(data.payment_amount) !== 0;

  /** A payment reference, linked only when there is something to link to. */
  const paymentRef = (label: string) =>
    hasOrderId ? (
      <span
        onClick={() =>
          onOpenPreview("payment", data.order_id as string | number)
        }
        className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
      >
        {label} #{data?.order_id}
      </span>
    ) : (
      <>{label}</>
    );

  /** "for $12.34", or nothing at all when there is no amount to show. */
  const amountClause = hasAmount ? (
    <>
      for <span>{formatCurrency(data?.payment_amount)}</span>
    </>
  ) : null;

  switch (data.event_type) {
    case "payment_agreement":
      if (data?.event_status === "sent" || data?.event_status === "created") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              sent
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.plan_id) return;

                onOpenPreview(
                  "agreement",
                  encode(
                    `${data?.plan_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "agr_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment agreement
            </span>{" "}
            to{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
          </p>
        );
      } else if (
        data?.event_status === "expired" ||
        data?.event_status === "replaced"
      ) {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.plan_id) return;

                onOpenPreview(
                  "agreement",
                  encode(
                    `${data?.plan_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "agr_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Payment agreement
            </span>{" "}
            sent to{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name?.[0] ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            by{" "}
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status === "expired" ? "expired" : "was replaced"}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "accepted") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              accepted{" "}
            </span>
            <span
              onClick={() => {
                if (!data || !data.plan_id) return;
                onOpenPreview(
                  "agreement",
                  encode(
                    `${data?.plan_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "agr_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment agreement
            </span>{" "}
            sent by{" "}
            {(data?.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
          </p>
        );
      } else if (
        data?.event_status === "modified" ||
        data?.event_status === "cancelled" ||
        data?.event_status === "canceled"
      ) {
        return (
          <p className="leading-[18.23px]">
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status === "modified" ? "updated" : "canceled"}{" "}
            </span>
            <span
              onClick={() => {
                if (!data || !data.plan_id) return;
                onOpenPreview(
                  "agreement",
                  encode(
                    `${data?.plan_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "agr_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment agreement
            </span>{" "}
            for{" "}
            {data?.initiated_by &&
            data?.initiated_by.toLowerCase() === "user" ? (
              <>
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              </>
            ) : (
              <span>{formatCurrency(data?.payment_amount)}</span>
            )}{" "}
          </p>
        );
      } else if (data?.event_status === "clicked") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has clicked
            </span>{" "}
            on{" "}
            <span
              onClick={() => {
                if (!data || !data.plan_id) return;
                onOpenPreview(
                  "agreement",
                  encode(
                    `${data?.plan_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "agr_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment agreement
            </span>{" "}
            sent by{" "}
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
          </p>
        );
      }
      break;
    case "payment_processed":
      if (
        data?.event_status === "succeeded" ||
        data?.event_status === "failed" ||
        data?.event_status === "accepted" ||
        data?.event_status === "succeeded/failed"
      ) {
        return (
          <p className="leading-[18.23px]">
            {paymentRef(data?.sch_id ? "Scheduled payment" : "Payment")}{" "}
            {amountClause} initiated by{" "}
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has {data?.event_status === "failed" ? "failed" : "succeeded"}
            </span>{" "}
          </p>
        );
      } else if (
        data?.event_status === "retry_failed" ||
        data?.event_status === "retry_succeeded"
      ) {
        return (
          <p className="leading-[18.23px]">
            {/* The space belongs inside the branch — leaving it outside put a
                double space in "Retry  for the" on the failed variant. */}
            Retry{data?.event_status === "retry_succeeded" ? " attempt" : ""}{" "}
            for the{" "}
            {/* sch_id is the schedule reference and is null for a one-off
                payment, so it tells us which wording the retry needs. */}
            {paymentRef(data?.sch_id ? "scheduled payment" : "payment")}{" "}
            {/* "initiated by", not "for": the name here is whoever triggered
                the retry — the customer from the portal or an agent from the
                extension — and the payment itself always belongs to the
                customer. */}
            {amountClause} initiated by{" "}
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status === "retry_failed" ? "failed" : "succeeded"}
            </span>
            .
          </p>
        );
      } else if (
        data?.event_status === "refund" ||
        data?.event_status === "Refunded"
      ) {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            has refunded {paymentRef("payment")} {amountClause}
          </p>
        );
      } else if (data?.event_status === "partial_refund") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            has partially refunded {paymentRef("payment")} {amountClause}
          </p>
        );
      } else if (
        data?.event_status === "void" ||
        data?.event_status === "voided"
      ) {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            voided {paymentRef("payment")} {amountClause}
          </p>
        );
      } else if (data?.event_status === "dispute_raised") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            disputed {paymentRef("payment")} {amountClause} processed by{" "}
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
          </p>
        );
      } else if (data?.event_status === "dispute_resolved") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.order_id) return;
                onOpenPreview("payment", data?.order_id);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Payment {data?.order_id}
            </span>{" "}
            {amountClause}
            disputed by{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            was resolved
          </p>
        );
      }
      break;
    case "payment_link":
      if (data?.event_status === "sent") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              sent
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.payment_link_id) return;
                onOpenPreview(
                  "payment_link",
                  encode(
                    `${data?.payment_link_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "pl_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment link
            </span>{" "}
            to{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "expired") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.payment_link_id) return;
                onOpenPreview(
                  "payment_link",
                  encode(
                    `${data?.payment_link_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "pl_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Payment link
            </span>{" "}
            sent to{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            by{" "}
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has expired
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "clicked") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has clicked
            </span>{" "}
            on{" "}
            <span
              onClick={() => {
                if (!data || !data.payment_link_id) return;
                onOpenPreview(
                  "payment_link",
                  encode(
                    `${data?.payment_link_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "pl_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment link
            </span>{" "}
            sent by{" "}
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
          </p>
        );
      } else if (data?.event_status === "requested") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              requested a new
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data?.payment_link_id) return;
                onOpenPreview(
                  "payment_link",
                  encode(
                    `${data?.payment_link_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                    "pl_"
                  )
                );
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              payment link
            </span>{" "}
          </p>
        );
      }
      break;
    case "payment_scheduled":
    case "custom_plan":
      if (data?.event_status === "created") {
        return (
          <p className="leading-[18.23px]">
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              created
            </span>{" "}
            <span
              onClick={() => {
                if (!data || (!data.order_id && !data.sch_id)) return;
                onOpenPreview("subscription", data.sch_id || data?.order_id);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              custom plan
            </span>{" "}
            {amountClause}
          </p>
        );
      } else if (
        data?.event_status === "modified" ||
        data?.event_status === "updated" ||
        data?.event_status === "canceled" ||
        data?.event_status === "accepted" ||
        data?.event_status === "succeeded"
      ) {
        return (
          <p className="leading-[18.23px]">
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status === "modified"
                ? "updated"
                : data?.event_status === "canceled"
                  ? "canceled"
                  : data?.event_status}
            </span>{" "}
            <span
              onClick={() => {
                if (!data || (!data.order_id && !data.sch_id)) return;
                onOpenPreview("subscription", data.sch_id || data?.order_id);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              custom plan
            </span>{" "}
            {amountClause}
          </p>
        );
      }
      break;
    case "invoice":
      if (data?.event_status === "created") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Invoice {data?.invoice_number}
            </span>{" "}
            was{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              created
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>
          </p>
        );
      } else if (data?.event_status === "updated") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status}
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              invoice {data?.invoice_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>
          </p>
        );
      } else if (
        data?.event_status === "canceled" ||
        data?.event_status === "cancelled"
      ) {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              canceled
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              invoice {data?.invoice_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>
          </p>
        );
      } else if (data?.event_status === "sent") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              sent
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              invoice {data?.invoice_number}
            </span>{" "}
            to{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "expired") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Invoice {data?.invoice_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has expired
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "clicked") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has clicked
            </span>{" "}
            on{" "}
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              invoice {data?.invoice_number}
            </span>{" "}
          </p>
        );
      } else if (
        data?.event_status === "paid" ||
        data?.event_status === "finalized"
      ) {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              paid
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.invoice_id) return;
                let slug = encode(
                  `${data?.invoice_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "inv_"
                );
                onOpenPreview("invoice", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              invoice {data?.invoice_number}
            </span>{" "}
          </p>
        );
      }
      break;
    case "quote":
      if (
        data?.event_status === "created" ||
        data?.event_status === "finalized"
      ) {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              created
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              quote {data?.quote_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "expired") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Quote {data?.quote_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              expired
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "clicked") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              has clicked
            </span>{" "}
            on{" "}
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              quote {data?.quote_number}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "accepted") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              accepted{" "}
            </span>
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              quote {data?.quote_number}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "declined") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              declined{" "}
            </span>
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              quote {data?.quote_number}.
            </span>{" "}
          </p>
        );
      } else if (
        data?.event_status === "canceled" ||
        data?.event_status === "cancelled"
      ) {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              canceled
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              quote {data?.quote_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
          </p>
        );
      } else if (data?.event_status === "updated") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              updated
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.quote_id) return;
                let slug = encode(
                  `${data?.quote_id}_${
                    accountData?.clusterid
                  }_${new Date().getTime()}`,
                  "qt_"
                );
                onOpenPreview("quotes", slug);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              quote {data?.quote_number}
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
          </p>
        );
      }
      break;
    case "contact":
      if (data?.event_status?.toLowerCase() === "modified") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            profile was updated
          </p>
        );
      } else if (data?.event_status?.toLowerCase() === "created") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            profile was created by{" "}
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
          </p>
        );
      } else if (data?.event_status?.toLowerCase() === "deleted") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            profile was deleted
          </p>
        );
      } else if (data?.event_status?.toLowerCase() === "added") {
        return (
          <p className="leading-[18.23px]">
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>{" "}
            profile was added
          </p>
        );
      }
      break;
    case "subscription":
      if (data?.event_status === "created") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.subscription_id) return;
                onOpenPreview("subscription", data.subscription_id);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Subscription
            </span>{" "}
            was{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              created
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>
          </p>
        );
      } else if (
        data?.event_status === "modified" ||
        data?.event_status === "updated" ||
        data?.event_status === "canceled" ||
        data?.event_status === "cancelled" ||
        data?.event_status === "accepted" ||
        data?.event_status === "succeeded"
      ) {
        return (
          <p className="leading-[18.23px]">
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status === "modified"
                ? "updated"
                : data?.event_status === "cancelled"
                  ? "canceled"
                  : data?.event_status}
            </span>{" "}
            <span
              onClick={() => {
                if (!data || !data.subscription_id) return;
                onOpenPreview("subscription", data.subscription_id);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              subscription
            </span>
          </p>
        );
      } else if (data?.event_status === "expired") {
        return (
          <p className="leading-[18.23px]">
            <span
              onClick={() => {
                if (!data || !data.subscription_id) return;
                onOpenPreview("subscription", data.subscription_id);
              }}
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
            >
              Subscription [${data?.subscription_id}]
            </span>{" "}
            for{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/my-profile`)}
            >
              {(data?.first_name || "") +
                " " +
                (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
              {accountIdLabel}
            </span>
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              expired
            </span>{" "}
            due to non-payment.
          </p>
        );
      }
      break;
    case "manual_payment":
      if (data?.event_status === "added") {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              added
            </span>{" "}
            {paymentRef("manual payment")} for{" "}
            <span>{formatCurrency(data?.payment_amount)}</span>.
          </p>
        );
      } else if (
        data?.event_status === "updated" ||
        data?.event_status === "modified"
      ) {
        return (
          <p className="leading-[18.23px]">
            {(data.user_first_name || "") +
              (data?.user_last_name ? ` ${data?.user_last_name[0]}.` : "")}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              updated
            </span>{" "}
            {paymentRef("manual payment")} for{" "}
            <span>{formatCurrency(data?.payment_amount)}</span>.
          </p>
        );
      }
      break;
    case "payment_method":
      if (data?.event_status === "added" || data?.event_status === "deleted") {
        return (
          <p className="leading-[18.23px]">
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              {data?.event_status === "added" ? "added" : "removed"}
            </span>{" "}
            {data?.comments} {data?.event_status === "added" ? "to " : "from "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => navigate(`/${cluster_id}/payment-methods`)}
            >
              Payment Methods
            </span>
            .
          </p>
        );
      } else if (data?.event_status === "updated") {
        return (
          <p className="leading-[18.23px]">
            {data?.initiated_by &&
              (data?.initiated_by.toLowerCase() === "user" ? (
                <>
                  {(data.user_first_name || "") +
                    (data?.user_last_name
                      ? ` ${data?.user_last_name[0]}.`
                      : "")}{" "}
                </>
              ) : (
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/my-profile`)}
                >
                  {(data?.first_name || "") +
                    " " +
                    (data?.last_name ? data?.last_name[0] + "." : "")}{" "}
                  {accountIdLabel}
                </span>
              ))}{" "}
            <span className="border-b-[0.7px] border-b-txtPrimary border-dashed text-txtPrimary">
              updated
            </span>{" "}
            {data?.comments && data?.comments !== "" ? (
              <>
                <span>{data?.comments}</span> from{" "}
                <span
                  className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
                  onClick={() => navigate(`/${cluster_id}/payment-methods`)}
                >
                  Payment Methods
                </span>
              </>
            ) : (
              <>the payment method</>
            )}
            .
          </p>
        );
      }
      break;
    case "offer_available":
      if (data?.event_status === "created") {
        return (
          <p className="leading-[18.23px]">
            You're eligible for special discounts.{" "}
            <span
              className="border-b-[1px] cursor-pointer border-b-txtPrimary font-medium text-txtPrimary"
              onClick={() => {
                if (data?.comments === "payment_agreement") {
                  if (!data || !data.plan_id) return;

                  onOpenPreview(
                    "agreement",
                    encode(
                      `${data?.plan_id}_${userData?.data?.accountData?.clusterid}_${new Date().getTime()}`,
                      "agr_"
                    )
                  );
                } else {
                  navigate(`/${cluster_id}/make-payment`);
                }
              }}
            >
              Click here
            </span>{" "}
            to explore your savings.
          </p>
        );
      }
      break;
    default:
      break;
  }

  return null;
};

export default NotificationMessage;
